import { usePostApi, type WriteApiOptions } from "@shared/api/hooks";
import type { RegisterRequest, RegisterResponse } from "@shared/contracts";
import { USER_REGISTER_SERVICE_NAME } from "../constants";

export type UseRegisterUserApiOptions = Omit<
  WriteApiOptions<RegisterResponse, RegisterRequest>,
  "serviceName" | "idRequired"
>;

/**
 * Register — `POST /v1/{actor}/register`, resolves
 * `{ user, token, refresh_token }`.
 *
 * Registration **signs the user in**: the backend issues a full token pair and
 * assigns the actor's role, so the caller should adopt the session rather than
 * routing to a login screen.
 *
 * There is no users list to invalidate (this backend exposes no `users`
 * resource), hence `invalidateKeys: false`.
 *
 * @example
 * ```typescript
 * const register = useRegisterUserApi({ onSuccess: (s) => setSession(s) });
 * register.mutate({ first_name, last_name, email, password, password_confirmation });
 * ```
 */
export function useRegisterUserApi(options?: UseRegisterUserApiOptions) {
  return usePostApi<RegisterResponse, RegisterRequest>({
    serviceName: USER_REGISTER_SERVICE_NAME,
    invalidateKeys: false,
    ...options,
  });
}
